import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import { useDirectionsRendererContext } from "../context/DirectionRendererContext";
import { useDispatch } from "react-redux";
import { missionControlActions } from "../store/mission-control/mission-control-slice";
import { addDirectionServiceAudit } from "../api/auditApi";

const useDirections = ({
  origin,
  destination,
  showRoutes,
  ds,
  advancedDirection = false,
  locationInfoLoading = false,
  locationInfo = [],
  nextLocation = null,
  handleAdvancedDirectionResponse = () => {},
  terminalId,
  avoidHighways,
  jobId,
}) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary("routes");
  const [directionService, setDirectionService] = useState(routesLibrary);
  const [routeSummary, setRouteSummary] = useState(null);
  const [directionResponse, setDirectionResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const directionRendererRef = useDirectionsRendererContext();
  const dispatch = useDispatch();

  useEffect(() => {
    if (ds === 1 && !directionRendererRef.current && map && routesLibrary) {
      directionRendererRef.current = new routesLibrary.DirectionsRenderer({
        map,
        suppressMarkers: true,
        preserveViewport: true,
      });
    }
  }, [directionRendererRef, ds, map, routesLibrary]);

  useEffect(() => {
    if (routesLibrary) {
      setDirectionService(new routesLibrary.DirectionsService());
    }
  }, [routesLibrary]);

  useEffect(() => {
    if (ds === 1 && directionRendererRef.current) {
      if (showRoutes) {
        if (directionResponse) {
          directionRendererRef.current.setMap(map);
          directionRendererRef.current.setDirections(directionResponse);
        }
      } else {
        directionRendererRef.current.setMap(null);
      }
    }
  }, [ds, directionResponse, map, showRoutes, directionRendererRef]);

  useEffect(() => {
    (async () => {
      if (ds === 1) {
        if (!directionService || !directionService.route) return;
        setIsLoading(true);
        const response = await directionService.route({
          origin,
          destination: destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
          avoidHighways,
        });

        addDirectionServiceAudit({
          user: "FE MISSION CONTROL",
          jobId: jobId,
          reqId: null,
          from: {
            locId: null,
            lon: origin?.lng,
            lat: origin?.lat,
          },
          to: {
            locId: null,
            lon: destination?.lng,
            lat: destination?.lat,
          },
        });

        dispatch(missionControlActions.incrementApiCount());
        console.log("DIRECTION FETCH --------> ", response);
        if (response.status === "OK") {
          setDirectionResponse(response);
          const routeDetails = response.routes[0].legs[0];
          setRouteSummary({
            distance: routeDetails.distance,
            duration: routeDetails.duration,
          });
        }
        setIsLoading(false);
      }
    })();
  }, [directionService, ds, avoidHighways]);

  useEffect(() => {
    (async () => {
      if (ds === 1 && advancedDirection && !locationInfoLoading) {
        console.log("IN ADVANCED LOCATION --> ");
        if (!directionService || !directionService.route) return;
        setIsLoading(true);
        let locInfo = [...locationInfo];
        const firstLocation = locInfo.shift();
        const lastLocation = locInfo.pop();
        const nextLocationIndex = locInfo.findIndex(
          (loc) => loc.locaId === nextLocation,
        );
        const firstWaypoints = locInfo.slice(0, nextLocationIndex);
        const secondWaypoints = locInfo.slice(nextLocationIndex);
        const locations = [
          {
            origin: {
              lat: firstLocation.locaLat,
              lng: firstLocation.locaLon,
            },
            destination: origin,
            travelMode: window.google.maps.TravelMode.DRIVING,
            waypoints: firstWaypoints.map((loc) => ({
              location: {
                lat: loc.locaLat,
                lng: loc.locaLon,
              },
              stopover: true,
            })),
            avoidHighways,
          },
          {
            origin,
            destination: {
              lat: lastLocation.locaLat,
              lng: lastLocation.locaLon,
            },
            travelMode: window.google.maps.TravelMode.DRIVING,
            waypoints: secondWaypoints.map((loc) => ({
              location: {
                lat: loc.locaLat,
                lng: loc.locaLon,
              },
              stopover: true,
            })),
            avoidHighways,
          },
        ];
        const segment1Location = locations[0];
        const segment2Location = locations[1];
        const segment1Response = await directionService.route(segment1Location);
        const segment2Response = await directionService.route(segment2Location);

        dispatch(missionControlActions.incrementApiCount());
        dispatch(missionControlActions.incrementApiCount());

        addDirectionServiceAudit({
          user: "FE MISSION CONTROL (Advanced Direction)",
          jobId: jobId,
          reqId: null,
          from: {
            locId: null,
            lon: segment1Location?.origin?.lng,
            lat: segment1Location?.origin?.lat,
          },
          to: {
            locId: null,
            lon: segment1Location?.destination?.lng,
            lat: segment1Location?.destination?.lat,
          },
        });

        addDirectionServiceAudit({
          user: "FE MISSION CONTROL (Advanced Direction)",
          jobId: jobId,
          reqId: null,
          from: {
            locId: null,
            lon: segment2Location?.origin?.lng,
            lat: segment2Location?.origin?.lat,
          },
          to: {
            locId: null,
            lon: segment2Location?.destination?.lng,
            lat: segment2Location?.destination?.lat,
          },
        });

        if (
          segment1Response.status === "OK" &&
          segment2Response.status === "OK"
        ) {
          handleAdvancedDirectionResponse(
            segment1Response,
            segment2Response,
            terminalId,
          );
        }
        setIsLoading(false);
      }
    })();
  }, [
    advancedDirection,
    directionService,
    ds,
    handleAdvancedDirectionResponse,
    locationInfo,
    locationInfoLoading,
    nextLocation,
    terminalId,
    avoidHighways,
    jobId,
  ]);

  return {
    routeSummary,
    isLoading,
  };
};

export default useDirections;
